import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Download,
  FolderKanban,
  Gauge,
  RotateCcw,
  Timer,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { SortableTh, sortRows, BulkActionsBar, PillGroup } from '@synotis/ui';
import { apiReports } from '../api/client.js';
import {
  PROJECTS_REPORT_SORT,
  PROJECT_PEOPLE_SORT,
  PROJECT_TIME_ENTRIES_SORT,
  useSortStore,
} from '../hooks/useSortStore.js';
import { exportRowsToCsv } from '../components/reports/exportCsv.js';
import { ProjectBurnChart, ProjectHoursChart } from '../components/reports/ProjectCharts.jsx';
import { HOURS_PER_DAY } from '../lib/units.js';
import {
  PROJECTS_REPORT_COLUMNS,
  loadHiddenColumns,
  resetHiddenColumns,
  saveHiddenColumns,
  tableColSpan,
  toggleHiddenColumn,
  visibleColumns,
} from '../lib/projectsReportColumns.js';

/**
 * synotis-support#13 — Rapports › Projets.
 *
 * Suivi des projets pour ceux qui les pilotent (périmètre `delegated` de #108,
 * borné serveur : ADMIN tout, responsable client, PM, manager hiérarchique du
 * PM). Liste des affaires avec jauge budget, rythme de saisie, planifié à venir
 * et date de bascule ; détail par affaire (courbe cumulée, saisi vs planifié,
 * heatmap personne × période, répartition par tâche et phase).
 *
 * Aucun coût, CJM ni marge (F-108-01) ; heures seules par personne.
 * L'affaire ouverte est portée par `?project=<id>` (CTRL+R rouvre le détail) ;
 * l'ouverture pousse une entrée d'historique (Précédent ramène à la liste) et
 * les filtres de la liste survivent au passage par le détail (sessionStorage).
 */

const UNIT_KEY = 'synolog.reports.projects.unit';
const FILTERS_KEY = 'synolog.reports.projects.filters';
const PROJECT_PARAM = 'project';
/** Marqueur d'historique : l'entrée `?project=` a été poussée par cet écran. */
const HISTORY_MARK = 'synologProjectReport';
const DEFAULT_FILTERS = { clientId: '', kind: 'billable', budgetFilter: 'all' };

const STATUS_COLOR = {
  ok: 'var(--success)',
  warning: 'var(--warning)',
  over: 'var(--danger)',
  none: 'var(--text-muted)',
};
const STATUS_LABEL = { ok: 'OK', warning: 'Attention', over: 'Dépassé', none: 'Sans budget' };

const nf0 = new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 1 });

const fmtChf = (n) => (n == null ? '—' : `${nf0.format(n)} CHF`);
/** Budget exploitable : un budget à 0 vaut « sans budget ». */
const hasBudget = (n) => n != null && n > 0;
const fmtBudget = (n) => (hasBudget(n) ? fmtChf(n) : '—');
/**
 * Pourcentage tronqué (pas arrondi) à une décimale : 89,96 % s'affiche 89,9 %
 * et non 90 %, pour rester cohérent avec la couleur du statut (seuils entiers,
 * comparés à la valeur réelle côté serveur).
 */
const fmtPct = (n) => (n == null ? '—' : `${nf1.format(Math.floor(n * 10 + 1e-9) / 10)} %`);
/** Valeur brute pour l'export CSV : nombre à point décimal, 2 décimales max. */
const raw2 = (n) => (n == null || !Number.isFinite(Number(n)) ? '' : Math.round(Number(n) * 100) / 100);

/** Date du jour en Europe/Zurich (`YYYY-MM-DD`), comme le serveur. */
function zurichTodayIso() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Zurich',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-CH', { timeZone: 'UTC' });
}
function makeFmtHours(unit) {
  return (h, short = false) => {
    const v = Number(h) || 0;
    if (unit === 'd') return `${nf1.format(v / HOURS_PER_DAY)}${short ? '' : ' j'}`;
    return `${nf1.format(v)}${short ? '' : ' h'}`;
  };
}
function bucketLabel(key, granularity) {
  const d = new Date(`${key}T00:00:00Z`);
  if (granularity === 'month') {
    return d.toLocaleDateString('fr-CH', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }
  return d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
}

function loadUnit() {
  try {
    const v = window.localStorage.getItem(UNIT_KEY);
    return v === 'd' ? 'd' : 'h';
  } catch {
    return 'h';
  }
}

function loadFilters() {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(FILTERS_KEY) ?? 'null');
    if (!v || typeof v !== 'object') return DEFAULT_FILTERS;
    return {
      clientId: typeof v.clientId === 'string' ? v.clientId : '',
      kind: v.kind === 'all' ? 'all' : 'billable',
      budgetFilter: ['ok', 'warning', 'over'].includes(v.budgetFilter) ? v.budgetFilter : 'all',
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function saveFilters(f) {
  try {
    window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(f));
  } catch {
    /* filtres non mémorisés, sans impact */
  }
}

function readProjectParam() {
  try {
    return new URLSearchParams(window.location.search).get(PROJECT_PARAM) || null;
  } catch {
    return null;
  }
}

/** `push` : nouvelle entrée d'historique (ouverture du détail) ; sinon remplacement. */
function writeProjectParam(id, { push = false } = {}) {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set(PROJECT_PARAM, id);
  else url.searchParams.delete(PROJECT_PARAM);
  const target = url.pathname + url.search + url.hash;
  if (push) window.history.pushState({ [HISTORY_MARK]: id }, '', target);
  else window.history.replaceState(null, '', target);
}

// ---------- Styles partagés ----------
const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
};
const tdStyle = { padding: '9px 12px', whiteSpace: 'nowrap' };
const numTd = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
// synotis-support#21 — liste des projets : padding horizontal resserré
// (12 → 8) pour limiter le défilement horizontal des 13 colonnes.
// Tableau en `border-collapse: separate` (sticky) : le filet entre lignes est
// porté par les cellules, une bordure de <tr> ne s'y dessine pas.
const rowBorder = '1px solid var(--glass-border)';
const listTd = { ...tdStyle, padding: '9px 8px', borderTop: rowBorder };
const listNumTd = { ...numTd, padding: '9px 8px', borderTop: rowBorder };
const listThStyle = { padding: '10px 8px', verticalAlign: 'bottom' };
// Client / PM : repliables sur plusieurs lignes au-delà de cette largeur.
const wrapTd = { ...listTd, whiteSpace: 'normal', color: 'var(--text-muted)' };
const wrapBox = { maxWidth: 160, overflowWrap: 'break-word' };
// Retours du 29/10 : en-tête figé en haut du conteneur, case à cocher et
// « Projet » figées à gauche. Fond opaque obligatoire (sinon le contenu qui
// défile transparaît) : `--glass` est translucide, on le pose sur `--card-bg`.
const CHECK_W = 36;
const TINTED_BG = 'linear-gradient(var(--glass), var(--glass)), var(--card-bg)';
const frozenSep = 'inset -1px 0 0 var(--glass-border)';
const headTh = { ...listThStyle, position: 'sticky', top: 0, zIndex: 2, background: TINTED_BG };
const checkBox = { width: CHECK_W, minWidth: CHECK_W, maxWidth: CHECK_W, boxSizing: 'border-box' };
const checkTh = { ...headTh, ...checkBox, left: 0, zIndex: 4 };
const projectTh = { ...headTh, left: CHECK_W, zIndex: 4, boxShadow: frozenSep };
const frozenTd = (selected) => ({ position: 'sticky', zIndex: 1, background: selected ? TINTED_BG : 'var(--card-bg)' });
/** Hauteur minimale du tableau quand la fenêtre est trop basse pour tout tenir. */
const TABLE_MIN_HEIGHT = 320;
const selectStyle = {
  height: 32,
  padding: '0 10px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--glass-border)',
  background: 'var(--glass)',
  color: 'var(--text)',
  fontSize: 13,
  maxWidth: 260,
};
const ghostBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-pill)',
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer',
};

function ColorDot({ color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: 999,
        background: color || 'var(--glass-border)',
        flexShrink: 0,
      }}
    />
  );
}

function Kpi({ Icon, label, value, sub, color }) {
  return (
    <div style={{ ...card, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {Icon ? <Icon size={13} /> : null}
        {label}
      </span>
      <span style={{ fontSize: 20, fontWeight: 600, color: color ?? 'var(--text)' }}>{value}</span>
      {sub ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sub}</span> : null}
    </div>
  );
}

function KpiGrid({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
      {children}
    </div>
  );
}

/** Jauge de consommation : barre colorée selon le statut + libellé du statut au survol. */
function Gauge2({ pct, status }) {
  if (pct == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const color = STATUS_COLOR[status] ?? 'var(--accent)';
  return (
    <div title={`${STATUS_LABEL[status] ?? ''} — ${fmtPct(pct)}`} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
      <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--glass)', border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%', background: color, borderRadius: 999 }} />
      </div>
      {status === 'over' || status === 'warning' ? <AlertTriangle size={13} color={color} aria-label={STATUS_LABEL[status]} /> : null}
    </div>
  );
}

/**
 * Libellé d'en-tête sur deux lignes (un mot par ligne). Le bouton de
 * `SortableTh` (@synotis/ui) impose `nowrap` : on le lève ici plutôt que dans
 * le paquet partagé, pour ne pas changer les autres tableaux.
 */
function HeadLabel({ children }) {
  return (
    <span style={{ display: 'inline-block', whiteSpace: 'normal', maxWidth: 'min-content', lineHeight: 1.2, textAlign: 'left' }}>
      {children}
    </span>
  );
}

/**
 * Choix des colonnes affichées (popover : pas une création/modification, donc
 * pas de modale). « Projet » reste toujours affichée.
 */
function ColumnsMenu({ hidden, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const total = PROJECTS_REPORT_COLUMNS.length;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        style={ghostBtn}
        onClick={() => setOpen((o) => !o)}
        title="Choisir les colonnes affichées"
      >
        <Columns3 size={14} /> Colonnes{hidden.length ? ` (${total - hidden.length}/${total})` : ''}
      </button>
      {open ? (
        <div
          role="group"
          aria-label="Colonnes affichées"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            zIndex: 20,
            minWidth: 210,
            padding: 8,
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {PROJECTS_REPORT_COLUMNS.map((c) => (
            <label
              key={c.key}
              title={c.hideable ? undefined : 'Toujours affichée'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 6px',
                fontSize: 13,
                color: c.hideable ? 'var(--text)' : 'var(--text-muted)',
                cursor: c.hideable ? 'pointer' : 'default',
              }}
            >
              <input
                type="checkbox"
                checked={!hidden.includes(c.key)}
                disabled={!c.hideable}
                onChange={() => onToggle(c.key)}
              />
              {c.label}
            </label>
          ))}
          <button
            type="button"
            style={{ ...ghostBtn, justifyContent: 'center', marginTop: 6, opacity: hidden.length ? 1 : 0.45 }}
            disabled={!hidden.length}
            onClick={onReset}
            title="Réafficher toutes les colonnes"
          >
            <RotateCcw size={14} /> Réinitialiser
          </button>
        </div>
      ) : null}
    </div>
  );
}

function UnitToggle({ unit, onChange }) {
  return (
    <PillGroup
      ariaLabel="Unité"
      value={unit}
      onChange={onChange}
      options={[
        { value: 'h', label: 'Heures' },
        { value: 'd', label: 'Jours' },
      ]}
    />
  );
}

function StateBox({ children, color }) {
  return <div style={{ ...card, padding: 20, color: color ?? 'var(--text-muted)' }}>{children}</div>;
}

// ============================================================================
// Vue
// ============================================================================

export default function ProjectsReportView() {
  const [selectedId, setSelectedId] = useState(readProjectParam);
  const [unit, setUnitState] = useState(loadUnit);
  const setUnit = useCallback((u) => {
    setUnitState(u);
    try {
      window.localStorage.setItem(UNIT_KEY, u);
    } catch {
      /* préférence non mémorisée, sans impact */
    }
  }, []);

  // Filtres de la liste levés ici : ils survivent à l'aller-retour par le
  // détail (la liste est démontée) et, via sessionStorage, au F5.
  const [filters, setFiltersState] = useState(loadFilters);
  const setFilters = useCallback((patch) => {
    setFiltersState((prev) => {
      const next = { ...prev, ...patch };
      saveFilters(next);
      return next;
    });
  }, []);

  // Ouverture = entrée d'historique : Précédent ramène à la liste.
  const open = (id) => {
    writeProjectParam(id, { push: true });
    setSelectedId(id);
  };
  // Retour : si l'entrée a été poussée par cet écran, on recule (popstate
  // remet l'état) ; sinon (détail ouvert par lien / F5) on remplace l'URL.
  const close = () => {
    if (window.history.state?.[HISTORY_MARK] === selectedId) {
      window.history.back();
      return;
    }
    writeProjectParam(null);
    setSelectedId(null);
  };

  // Précédent/Suivant du navigateur : resynchronise l'affaire ouverte.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    const onPop = () => setSelectedId(readProjectParam());
    window.addEventListener('popstate', onPop);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('popstate', onPop);
      // On quitte l'écran : `?project=` ne doit pas suivre vers une autre vue
      // (`syncUrlToView` conserve la query string). Différé : en StrictMode le
      // démontage simulé est suivi d'un remontage immédiat, qui annule le
      // nettoyage — sinon `?project=` disparaissait dès le montage.
      setTimeout(() => {
        if (!mountedRef.current && readProjectParam()) writeProjectParam(null);
      }, 0);
    };
  }, []);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1500, margin: '0 auto' }}>
      {selectedId ? (
        <ProjectDetail id={selectedId} onBack={close} unit={unit} setUnit={setUnit} />
      ) : (
        <ProjectList onOpen={open} unit={unit} setUnit={setUnit} filters={filters} setFilters={setFilters} />
      )}
    </div>
  );
}

// ============================================================================
// Liste
// ============================================================================

const KIND_OPTIONS = [
  { value: 'billable', label: 'Facturables' },
  { value: 'all', label: 'Toutes' },
];
const BUDGET_OPTIONS = [
  { value: 'all', label: 'Tous' },
  { value: 'ok', label: 'OK' },
  { value: 'warning', label: 'Attention' },
  { value: 'over', label: 'Dépassé' },
];

function ProjectList({ onOpen, unit, setUnit, filters, setFilters }) {
  const { clientId, kind, budgetFilter } = filters;
  const setClientId = (v) => setFilters({ clientId: v });
  const setKind = (v) => setFilters({ kind: v });
  const setBudgetFilter = (v) => setFilters({ budgetFilter: v });
  const [data, setData] = useState({ rows: [], clients: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const { sort, setSort } = useSortStore(PROJECTS_REPORT_SORT);
  const fmtH = makeFmtHours(unit);

  // Colonnes masquées : durable sur ce navigateur, jusqu'à « Réinitialiser ».
  // Une colonne triée puis masquée garde son tri (aucune erreur, pas de saut).
  const [hiddenCols, setHiddenCols] = useState(() => loadHiddenColumns());
  const toggleColumn = (key) => {
    const next = toggleHiddenColumn(hiddenCols, key);
    saveHiddenColumns(next);
    setHiddenCols(next);
  };
  const resetColumns = () => setHiddenCols(resetHiddenColumns());
  const shownCols = visibleColumns(hiddenCols);
  const colSpan = tableColSpan(hiddenCols);

  // Le tableau défile dans son propre conteneur, borné à la hauteur restante
  // de la fenêtre : la barre horizontale est visible sans descendre en bas de
  // la page. Hauteur mesurée (en-têtes, KPI et barre d'actions variables),
  // indépendante du défilement courant de <main>.
  const tableBoxRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState(null);
  const hasSelection = selectedIds.size > 0;
  useLayoutEffect(() => {
    const el = tableBoxRef.current;
    if (!el) return undefined;
    const measure = () => {
      const scroller = el.closest('main');
      const box = el.getBoundingClientRect();
      const offset = scroller
        ? box.top - scroller.getBoundingClientRect().top + scroller.scrollTop
        : box.top + window.scrollY;
      const viewport = scroller ? scroller.clientHeight : window.innerHeight;
      // 24 px : marge basse de la vue (padding du conteneur racine).
      setTableMaxHeight(Math.max(TABLE_MIN_HEIGHT, Math.floor(viewport - offset - 24)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [error, loading, hasSelection]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiReports
      .projects({ clientId: clientId || undefined, kind })
      .then((res) => {
        if (cancelled) return;
        setData({ rows: res?.rows ?? [], clients: res?.clients ?? [] });
      })
      .catch((e) => {
        if (cancelled) return;
        setData({ rows: [], clients: [] });
        setError(e?.message || 'Chargement impossible');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, kind]);

  // Le client choisi n'est plus dans la liste (changement de type) : on le relâche.
  useEffect(() => {
    if (clientId && !loading && !error && !data.clients.some((c) => c.id === clientId)) {
      setFilters({ clientId: '' });
    }
  }, [clientId, data.clients, loading, error, setFilters]);

  const rows = useMemo(
    () => (budgetFilter === 'all' ? data.rows : data.rows.filter((r) => r.budgetStatus === budgetFilter)),
    [data.rows, budgetFilter],
  );
  const sorted = useMemo(
    () =>
      sortRows(rows, sort, {
        name: (r) => r.name,
        client: (r) => r.clientName,
        pm: (r) => r.projectManager?.name ?? null,
        budget: (r) => (hasBudget(r.budgetTotal) ? r.budgetTotal : null),
        consumed: (r) => r.consumedChf,
        consumptionPct: (r) => r.consumptionPct,
        remaining: (r) => r.remainingChf,
        logged30d: (r) => r.loggedHours30d,
        planned: (r) => r.plannedHoursRemaining,
        people: (r) => r.activePeople30d,
        last: (r) => r.lastLoggedDate,
        switchDate: (r) => r.switchDate,
      }),
    [rows, sort],
  );

  const kpi = useMemo(() => {
    let budget = 0;
    let consumed = 0;
    let consumedBudgeted = 0;
    let alerts = 0;
    for (const r of rows) {
      consumed += r.consumedChf;
      if (hasBudget(r.budgetTotal)) {
        budget += r.budgetTotal;
        consumedBudgeted += r.consumedChf;
      }
      if (r.budgetStatus === 'warning' || r.budgetStatus === 'over') alerts += 1;
    }
    return { budget, consumed, consumedBudgeted, alerts };
  }, [rows]);

  // Filtre changé (client, type, statut budget) ou rechargement : la sélection
  // ne garde que les lignes encore affichées — compteur et export cohérents.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const allSelected = sorted.length > 0 && sorted.every((r) => selectedIds.has(r.id));
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(sorted.map((r) => r.id)));
  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Export : toutes les colonnes numériques en valeurs BRUTES non formatées,
  // point décimal, 2 décimales max (pas de séparateur de milliers) ; dates ISO.
  // Heures ou jours selon l'unité affichée. Budget nul = cellule vide.
  const unitValue = (v) => raw2(unit === 'd' ? (Number(v) || 0) / HOURS_PER_DAY : v);
  const csvColumns = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Projet' },
    { key: 'clientName', label: 'Client' },
    { key: 'projectManager', label: 'Project manager', format: (v) => v?.name ?? '' },
    { key: 'status', label: 'Statut' },
    { key: 'startDate', label: 'Début' },
    { key: 'endDate', label: 'Fin' },
    { key: 'budgetTotal', label: 'Budget (CHF)', format: (v) => (hasBudget(v) ? raw2(v) : '') },
    { key: 'consumedChf', label: 'Consommé (CHF)', format: raw2 },
    { key: 'consumptionPct', label: 'Consommé (%)', format: raw2 },
    { key: 'budgetStatus', label: 'Statut budget', format: (v) => STATUS_LABEL[v] ?? v },
    { key: 'remainingChf', label: 'Reste (CHF)', format: raw2 },
    { key: 'loggedHoursTotal', label: `Saisi total (${unit})`, format: unitValue },
    { key: 'loggedHours30d', label: `Saisi 30 j (${unit})`, format: unitValue },
    { key: 'plannedHoursRemaining', label: `Planifié restant (${unit})`, format: unitValue },
    { key: 'projectedTotalChf', label: 'Projeté (CHF)', format: raw2 },
    { key: 'activePeople30d', label: 'Personnes actives 30 j', format: raw2 },
    { key: 'lastLoggedDate', label: 'Dernière saisie' },
    { key: 'switchDate', label: 'Bascule prévue' },
  ];
  const exportCsv = (list) => exportRowsToCsv(list, csvColumns, `rapport_projets_${zurichTodayIso()}`);
  const selectedRows = sorted.filter((r) => selectedIds.has(r.id));

  // Rendu par colonne ; l'ordre et la visibilité viennent de
  // `PROJECTS_REPORT_COLUMNS` (clé = champ de tri).
  const columnView = {
    name: {
      head: 'Projet',
      cell: (r, selected) => (
        <td style={{ ...listTd, whiteSpace: 'normal', minWidth: 150, ...frozenTd(selected), left: CHECK_W, boxShadow: frozenSep }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 220 }}>
            <ColorDot color={r.color} />
            <div style={{ minWidth: 0, overflowWrap: 'break-word' }}>
              <div style={{ color: 'var(--text)', fontWeight: 500 }}>{r.name}</div>
              {r.code ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.code}</div> : null}
            </div>
          </div>
        </td>
      ),
    },
    client: { head: 'Client', cell: (r) => <td style={wrapTd}><div style={wrapBox}>{r.clientName ?? '—'}</div></td> },
    pm: { head: 'PM', cell: (r) => <td style={wrapTd}><div style={wrapBox}>{r.projectManager?.name ?? '—'}</div></td> },
    budget: {
      head: 'Budget',
      cell: (r) => (
        <td style={{ ...listNumTd, color: hasBudget(r.budgetTotal) ? undefined : 'var(--text-muted)' }}>
          {fmtBudget(r.budgetTotal)}
        </td>
      ),
    },
    consumed: {
      head: 'Consommé',
      cell: (r) => (
        <td style={{ ...listTd, minWidth: 130 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtChf(r.consumedChf)}</span>
            <Gauge2 pct={r.consumptionPct} status={r.budgetStatus} />
          </div>
        </td>
      ),
    },
    consumptionPct: {
      head: '%',
      cell: (r) => <td style={{ ...listNumTd, color: STATUS_COLOR[r.budgetStatus] }}>{fmtPct(r.consumptionPct)}</td>,
    },
    remaining: {
      head: 'Reste',
      cell: (r) => (
        <td style={{ ...listNumTd, color: r.remainingChf != null && r.remainingChf < 0 ? 'var(--danger)' : undefined }}>
          {fmtChf(r.remainingChf)}
        </td>
      ),
    },
    // Espace insécable dans « 30 j » : deux lignes, pas trois.
    logged30d: { head: <HeadLabel>Saisi 30&nbsp;j</HeadLabel>, cell: (r) => <td style={listNumTd}>{fmtH(r.loggedHours30d)}</td> },
    planned: { head: <HeadLabel>Planifié restant</HeadLabel>, cell: (r) => <td style={listNumTd}>{fmtH(r.plannedHoursRemaining)}</td> },
    people: { head: <HeadLabel>Actifs 30&nbsp;j</HeadLabel>, cell: (r) => <td style={listNumTd}>{r.activePeople30d}</td> },
    last: { head: <HeadLabel>Dernière saisie</HeadLabel>, cell: (r) => <td style={listNumTd}>{fmtDate(r.lastLoggedDate)}</td> },
    switchDate: {
      head: <HeadLabel>Bascule prévue</HeadLabel>,
      cell: (r) => (
        <td style={{ ...listNumTd, color: r.switchDate ? 'var(--danger)' : 'var(--text-muted)' }}>{fmtDate(r.switchDate)}</td>
      ),
    },
  };

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <FolderKanban size={20} /> Projets
        </h1>
        <select
          aria-label="Client"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          style={selectStyle}
        >
          <option value="">Tous les clients</option>
          {data.clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <PillGroup ariaLabel="Type d’affaires" value={kind} onChange={setKind} options={KIND_OPTIONS} />
        <PillGroup ariaLabel="Statut budget" value={budgetFilter} onChange={setBudgetFilter} options={BUDGET_OPTIONS} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <UnitToggle unit={unit} onChange={setUnit} />
          <ColumnsMenu hidden={hiddenCols} onToggle={toggleColumn} onReset={resetColumns} />
          <button
            type="button"
            style={{ ...ghostBtn, opacity: sorted.length ? 1 : 0.45 }}
            disabled={!sorted.length}
            onClick={() => exportCsv(sorted)}
            title="Exporter toutes les lignes affichées, avec toutes les colonnes même masquées (CSV)"
          >
            <Download size={14} /> Exporter
          </button>
        </div>
      </div>

      {error ? null : (
        <KpiGrid>
          <Kpi Icon={FolderKanban} label="Projets" value={nf0.format(rows.length)} />
          <Kpi Icon={Wallet} label="Budget total" value={fmtChf(kpi.budget)} sub="Affaires budgétées" />
          <Kpi
            Icon={Gauge}
            label="Consommé"
            value={fmtChf(kpi.consumed)}
            sub={kpi.budget > 0 ? `${fmtPct((kpi.consumedBudgeted / kpi.budget) * 100)} des budgets` : null}
          />
          <Kpi
            Icon={AlertTriangle}
            label="En alerte"
            value={nf0.format(kpi.alerts)}
            color={kpi.alerts ? 'var(--warning)' : undefined}
            sub="Attention ou dépassé"
          />
        </KpiGrid>
      )}

      <BulkActionsBar
        count={selectedIds.size}
        actions={[
          { key: 'csv', label: 'Exporter (CSV)', icon: Download, count: selectedRows.length, onClick: () => exportCsv(selectedRows) },
        ]}
        onClear={() => setSelectedIds(new Set())}
      />

      <div
        ref={tableBoxRef}
        style={{ ...card, maxHeight: tableMaxHeight ?? 'calc(100vh - 180px)', overflow: 'auto' }}
      >
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left' }}>
              <th style={checkTh}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={sorted.length === 0}
                  onChange={toggleAll}
                  title="Tout sélectionner"
                  aria-label="Tout sélectionner"
                />
              </th>
              {shownCols.map((c) => (
                <SortableTh key={c.key} field={c.key} sort={sort} onSort={setSort} style={c.key === 'name' ? projectTh : headTh}>
                  {columnView[c.key].head}
                </SortableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : error ? (
              // Erreur : seul le message, jamais « Aucun projet » en plus.
              <tr>
                <td colSpan={colSpan} role="alert" style={{ padding: 20, color: 'var(--danger)' }}>
                  {error}
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={colSpan} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Aucun projet dans votre périmètre pour ces filtres.
                </td>
              </tr>
            ) : (
              sorted.map((r) => {
                const selected = selectedIds.has(r.id);
                return (
                  <tr
                    key={r.id}
                    onClick={() => onOpen(r.id)}
                    style={{ cursor: 'pointer', background: selected ? 'var(--glass)' : undefined }}
                    title="Ouvrir le détail du projet"
                  >
                    <td style={{ ...listTd, ...checkBox, ...frozenTd(selected), left: 0 }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleOne(r.id)}
                        aria-label={`Sélectionner ${r.name}`}
                      />
                    </td>
                    {shownCols.map((c) => (
                      <Fragment key={c.key}>{columnView[c.key].cell(r, selected)}</Fragment>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ============================================================================
// Détail
// ============================================================================

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Depuis le début' },
  { value: '3m', label: '3 derniers mois' },
  { value: '12m', label: '12 derniers mois' },
];
const GRANULARITY_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
];

/** Fenêtre d'un préréglage ; `all` = bornes par défaut du serveur (vie du projet). */
function periodWindow(key) {
  if (key === 'all') return {};
  // Aujourd'hui en Europe/Zurich, comme le serveur (fenêtre 30 j, repère, horizon).
  const today = new Date(`${zurichTodayIso()}T00:00:00Z`);
  const from = new Date(today);
  if (key === '3m') from.setUTCMonth(from.getUTCMonth() - 3);
  else from.setUTCFullYear(from.getUTCFullYear() - 1);
  // On garde 8 semaines devant aujourd'hui pour voir la projection.
  const to = new Date(today.getTime() + 56 * 86_400_000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function ProjectDetail({ id, onBack, unit, setUnit }) {
  const [period, setPeriod] = useState('all');
  const [granularity, setGranularity] = useState('auto');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fmtH = makeFmtHours(unit);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiReports
      .projectDetail(id, { ...periodWindow(period), granularity: granularity === 'auto' ? undefined : granularity })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setData(null);
        setError(e?.status === 404 ? 'Projet introuvable ou hors de votre périmètre.' : e?.message || 'Chargement impossible');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, period, granularity]);

  const s = data?.summary;
  const gran = data?.granularity ?? 'week';
  const label = (k) => bucketLabel(k, gran);

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <button type="button" onClick={onBack} style={ghostBtn} title="Retour à la liste des projets">
          <ArrowLeft size={14} /> Projets
        </button>
        <h1 style={{ margin: 0, fontSize: 20, color: 'var(--text)', display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {s ? <ColorDot color={s.color} /> : null}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{s?.name ?? 'Projet'}</span>
          {s?.code ? <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>{s.code}</span> : null}
        </h1>
        {s ? (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {s.clientName ?? '—'} · PM {s.projectManager?.name ?? '—'} · {fmtDate(s.startDate)} → {fmtDate(s.endDate)}
          </span>
        ) : null}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <PillGroup ariaLabel="Période" value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
          <PillGroup ariaLabel="Granularité" value={granularity} onChange={setGranularity} options={GRANULARITY_OPTIONS} />
          <UnitToggle unit={unit} onChange={setUnit} />
        </div>
      </div>

      {error ? (
        <StateBox color="var(--danger)">{error}</StateBox>
      ) : loading && !data ? (
        <StateBox>Chargement…</StateBox>
      ) : !s ? null : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }}>
          <KpiGrid>
            <Kpi
              Icon={Wallet}
              label="Budget"
              value={fmtBudget(s.budgetTotal)}
              sub={hasBudget(s.budgetTotal) ? `Reste ${fmtChf(s.remainingChf)}` : 'Aucun budget'}
            />
            <Kpi
              Icon={Gauge}
              label="Consommé"
              value={fmtChf(s.consumedChf)}
              sub={`${fmtPct(s.consumptionPct)} · ${fmtH(s.loggedHoursTotal)} saisis`}
              color={s.budgetStatus === 'none' ? undefined : STATUS_COLOR[s.budgetStatus]}
            />
            <Kpi
              Icon={TrendingUp}
              label="Projeté"
              value={fmtChf(s.projectedTotalChf)}
              sub={`${fmtPct(s.projectedPct)} · + ${fmtH(s.plannedHoursRemaining)} planifiés`}
              color={s.projectedPct != null && s.projectedPct > 100 ? 'var(--danger)' : undefined}
            />
            <Kpi
              Icon={CalendarClock}
              label="Bascule prévue"
              value={s.switchDate ? fmtDate(s.switchDate) : '—'}
              sub={s.switchDate ? 'Budget dépassé selon le planifié' : 'Pas de dépassement planifié'}
              color={s.switchDate ? 'var(--danger)' : undefined}
            />
            <Kpi
              Icon={Timer}
              label={`Rythme (${nf1.format(s.burnWeeks ?? 4)} sem.)`}
              value={`${fmtH(s.weeklyBurnHours)} / sem.`}
              sub={`${fmtChf(s.weeklyBurnChf)} / sem.`}
            />
            <Kpi
              Icon={CalendarClock}
              label="Fin de budget au rythme actuel"
              value={
                s.remainingChf != null && s.remainingChf <= 0
                  ? 'Atteinte'
                  : s.projectedExhaustionDate
                    ? fmtDate(s.projectedExhaustionDate)
                    : '—'
              }
              color={s.remainingChf != null && s.remainingChf <= 0 ? 'var(--danger)' : undefined}
              sub={s.endDate ? `Fin prévue ${fmtDate(s.endDate)}` : null}
            />
            <Kpi Icon={Users} label="Actifs 30 j" value={nf0.format(s.activePeople30d)} sub={`Dernière saisie ${fmtDate(s.lastLoggedDate)}`} />
          </KpiGrid>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 16, marginBottom: 16 }}>
            <section style={{ ...card, padding: 14 }}>
              <h2 style={{ margin: '0 0 8px', fontSize: 15, color: 'var(--text)' }}>Consommation cumulée (CHF)</h2>
              {data.series.length ? (
                <ProjectBurnChart
                  series={data.series}
                  budgetTotal={data.budgetTotal}
                  today={data.today}
                  formatLabel={label}
                  formatChf={fmtChf}
                />
              ) : (
                <div style={{ color: 'var(--text-muted)' }}>Aucune donnée sur la période.</div>
              )}
            </section>
            <section style={{ ...card, padding: 14 }}>
              <h2 style={{ margin: '0 0 8px', fontSize: 15, color: 'var(--text)' }}>
                Saisi vs planifié ({unit === 'd' ? 'jours' : 'heures'})
              </h2>
              {data.series.length ? (
                <ProjectHoursChart
                  series={data.series}
                  formatLabel={label}
                  formatHours={fmtH}
                />
              ) : (
                <div style={{ color: 'var(--text-muted)' }}>Aucune donnée sur la période.</div>
              )}
            </section>
          </div>

          <PeopleHeatmap data={data} fmtH={fmtH} label={label} />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 16, marginTop: 16 }}>
            <Breakdown title="Par tâche" rows={data.tasks} fmtH={fmtH} />
            <Breakdown title="Par phase" rows={data.phases} fmtH={fmtH} />
          </div>

          <ProjectTimeEntries key={id} affaireId={id}projectLabel={s.code || s.name} unit={unit} fmtH={fmtH} />
        </div>
      )}
    </>
  );
}

/** Heatmap personne × période : heures saisies, intensité via `color-mix` sur l'accent. */
function PeopleHeatmap({ data, fmtH, label }) {
  const { sort, setSort } = useSortStore(PROJECT_PEOPLE_SORT);
  const buckets = data.series.map((p) => p.bucket);
  const max = useMemo(() => {
    let m = 0;
    for (const p of data.people) for (const v of Object.values(p.buckets)) if (v > m) m = v;
    return m;
  }, [data.people]);
  const rows = useMemo(
    () =>
      sortRows(data.people, sort, {
        name: (p) => p.name,
        totalHours: (p) => p.totalHours,
        periodHours: (p) => p.periodHours,
        plannedFutureHours: (p) => p.plannedFutureHours,
        lastLoggedDate: (p) => p.lastLoggedDate,
      }),
    [data.people, sort],
  );
  const cellBg = (h) => {
    if (!h || !max) return undefined;
    const pct = Math.round(12 + (h / max) * 63);
    return `color-mix(in srgb, var(--accent) ${pct}%, transparent)`;
  };

  return (
    <section style={{ ...card, padding: 14 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--text)' }}>Qui saisit, quand</h2>
      <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text-muted)' }}>
        Temps saisi par personne et par période (plus la case est foncée, plus le volume est élevé).
      </p>
      {rows.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>Aucune saisie ni planification sur ce projet.</div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <SortableTh field="name" sort={sort} onSort={setSort} style={{ position: 'sticky', left: 0, background: 'var(--card-bg)', zIndex: 1 }}>
                  Personne
                </SortableTh>
                <SortableTh field="totalHours" sort={sort} onSort={setSort}>Total</SortableTh>
                <SortableTh field="periodHours" sort={sort} onSort={setSort}>Période</SortableTh>
                <SortableTh field="plannedFutureHours" sort={sort} onSort={setSort}>Planifié à venir</SortableTh>
                <SortableTh field="lastLoggedDate" sort={sort} onSort={setSort}>Dernière saisie</SortableTh>
                {buckets.map((b) => (
                  <th key={b} style={{ padding: '4px 2px', fontWeight: 500, color: 'var(--text-muted)', fontSize: 10, textAlign: 'center', minWidth: 38 }}>
                    {label(b)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.personId}>
                  <td style={{ ...tdStyle, position: 'sticky', left: 0, background: 'var(--card-bg)', color: 'var(--text)', zIndex: 1 }}>{p.name}</td>
                  <td style={numTd}>{fmtH(p.totalHours)}</td>
                  <td style={numTd}>{fmtH(p.periodHours)}</td>
                  <td style={numTd}>{fmtH(p.plannedFutureHours)}</td>
                  <td style={numTd}>{fmtDate(p.lastLoggedDate)}</td>
                  {buckets.map((b) => {
                    const h = p.buckets[b] ?? 0;
                    return (
                      <td
                        key={b}
                        title={`${p.name} — ${label(b)} : ${fmtH(h)}`}
                        style={{
                          minWidth: 38,
                          height: 26,
                          textAlign: 'center',
                          borderRadius: 4,
                          background: cellBg(h) ?? 'var(--glass)',
                          color: 'var(--text)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {h ? fmtH(h, true) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const TIME_ENTRIES_PAGE_SIZE = 50;
/** Pages de l'export : taille maximale acceptée par l'API. */
const TIME_ENTRIES_EXPORT_PAGE_SIZE = 500;
/** Borne de l'export (100 requêtes) : au-delà, l'export est tronqué et le dit. */
const TIME_ENTRIES_EXPORT_MAX = 50_000;
const TIME_ENTRIES_SORT_FIELDS = ['date', 'person', 'hours', 'task'];

/**
 * synotis-support#34 — liste détaillée des imputations du projet, sur toute sa
 * durée (indépendante du sélecteur de période). Consultation seule : ni
 * modification, ni suppression, donc ni multisélection ni actions groupées.
 * Tri et pagination côté serveur (le tri porte sur toute la liste) ; l'export
 * CSV récupère toutes les pages, pas seulement celle affichée. Heures seules.
 */
export function ProjectTimeEntries({ affaireId, projectLabel, unit, fmtH }) {
  const { sort: storedSort, setSort: setStoredSort } = useSortStore(PROJECT_TIME_ENTRIES_SORT);
  const sort = TIME_ENTRIES_SORT_FIELDS.includes(storedSort.field) ? storedSort : PROJECT_TIME_ENTRIES_SORT.fallback;
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const setSort = (next) => {
    setStoredSort(next);
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiReports
      .projectTimeEntries(affaireId, { sort: sort.field, dir: sort.dir, page, pageSize: TIME_ENTRIES_PAGE_SIZE })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Chargement impossible');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [affaireId, sort.field, sort.dir, page]);

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / TIME_ENTRIES_PAGE_SIZE));
  const first = total ? (page - 1) * TIME_ENTRIES_PAGE_SIZE + 1 : 0;
  const last = Math.min(total, page * TIME_ENTRIES_PAGE_SIZE);

  const unitValue = (v) => raw2(unit === 'd' ? (Number(v) || 0) / HOURS_PER_DAY : v);
  const csvColumns = [
    { key: 'date', label: 'Date' },
    { key: 'personName', label: 'Personne' },
    { key: 'hours', label: `Heures (${unit})`, format: unitValue },
    { key: 'billable', label: 'Facturable', format: (v) => (v ? 'Oui' : 'Non') },
    { key: 'taskName', label: 'Tâche', format: (v) => v ?? '' },
    { key: 'phaseName', label: 'Phase', format: (v) => v ?? '' },
    { key: 'notes', label: 'Commentaire', format: (v) => v ?? '' },
  ];

  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const all = [];
      let expected = Infinity;
      for (let p = 1; all.length < Math.min(expected, TIME_ENTRIES_EXPORT_MAX); p += 1) {
        const res = await apiReports.projectTimeEntries(affaireId, {
          sort: sort.field,
          dir: sort.dir,
          page: p,
          pageSize: TIME_ENTRIES_EXPORT_PAGE_SIZE,
        });
        expected = res.total;
        if (!res.rows.length) break;
        all.push(...res.rows);
      }
      exportRowsToCsv(
        all.slice(0, TIME_ENTRIES_EXPORT_MAX),
        csvColumns,
        `imputations_${projectLabel || 'projet'}_${zurichTodayIso()}`,
      );
      if (expected > TIME_ENTRIES_EXPORT_MAX) {
        setExportError(
          `Export limité aux ${nf0.format(TIME_ENTRIES_EXPORT_MAX)} premières lignes sur ${nf0.format(expected)}.`,
        );
      }
    } catch (e) {
      setExportError(e?.message || 'Export impossible');
    } finally {
      setExporting(false);
    }
  };

  const pagerBtn = (disabled) => ({ ...ghostBtn, padding: '4px 10px', opacity: disabled ? 0.45 : 1, cursor: disabled ? 'default' : 'pointer' });
  const entryTh = { ...listThStyle, textAlign: 'left' };

  return (
    <section style={{ ...card, padding: 14, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--text)' }}>Imputations</h2>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            Toutes les saisies du projet depuis le début, en consultation seule
            {data ? ` · ${nf0.format(total)} saisie${total > 1 ? 's' : ''}, ${fmtH(data.totalHours)}` : ''}.
          </p>
        </div>
        <button
          type="button"
          style={{ ...ghostBtn, marginLeft: 'auto', opacity: total && !exporting ? 1 : 0.45 }}
          disabled={!total || exporting}
          onClick={exportCsv}
          title="Exporter toutes les imputations du projet, toutes pages confondues (CSV)"
        >
          <Download size={14} /> {exporting ? 'Export…' : 'Exporter'}
        </button>
      </div>
      {exportError ? <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{exportError}</div> : null}
      {error ? (
        <div style={{ color: 'var(--danger)' }}>{error}</div>
      ) : (
        <div style={{ overflow: 'auto', opacity: loading && data ? 0.6 : 1, transition: 'opacity 120ms' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}>
            <thead>
              <tr>
                <SortableTh field="date" sort={sort} onSort={setSort} style={entryTh}>Date</SortableTh>
                <SortableTh field="person" sort={sort} onSort={setSort} style={entryTh}>Personne</SortableTh>
                <SortableTh field="hours" sort={sort} onSort={setSort} style={{ ...listThStyle, textAlign: 'right' }}>
                  {unit === 'd' ? 'Jours' : 'Heures'}
                </SortableTh>
                <SortableTh field="task" sort={sort} onSort={setSort} style={entryTh}>Tâche</SortableTh>
                <th style={{ ...entryTh, fontWeight: 500, color: 'var(--text-muted)' }}>Phase</th>
                <th style={{ ...entryTh, fontWeight: 500, color: 'var(--text-muted)' }}>Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {!data && loading ? (
                <tr>
                  <td colSpan={6} style={{ ...listTd, color: 'var(--text-muted)' }}>Chargement…</td>
                </tr>
              ) : total === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...listTd, color: 'var(--text-muted)' }}>Aucune saisie sur ce projet.</td>
                </tr>
              ) : (
                data.rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...listTd, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(r.date)}</td>
                    <td style={{ ...listTd, color: 'var(--text)' }}>{r.personName}</td>
                    <td style={listNumTd} title={r.billable ? 'Facturable' : 'Non facturable'}>
                      {fmtH(r.hours)}
                      {r.billable ? null : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · NF</span>}
                    </td>
                    <td style={{ ...listTd, color: r.taskName ? 'var(--text)' : 'var(--text-muted)' }}>{r.taskName ?? 'Sans tâche'}</td>
                    <td style={{ ...listTd, color: 'var(--text-muted)' }}>{r.phaseName ?? '—'}</td>
                    <td style={wrapTd}>
                      <div style={{ maxWidth: 360, overflowWrap: 'break-word' }}>{r.notes || '—'}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {total > TIME_ENTRIES_PAGE_SIZE ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
          <span>
            {nf0.format(first)}–{nf0.format(last)} sur {nf0.format(total)}
          </span>
          <button
            type="button"
            style={pagerBtn(page <= 1)}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            title="Page précédente"
            aria-label="Page précédente"
          >
            <ChevronLeft size={14} />
          </button>
          <span>
            Page {page} / {pageCount}
          </span>
          <button
            type="button"
            style={pagerBtn(page >= pageCount)}
            disabled={page >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            title="Page suivante"
            aria-label="Page suivante"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** Répartition des heures saisies (barres horizontales, total historique + période). */
function Breakdown({ title, rows, fmtH }) {
  const max = Math.max(0, ...rows.map((r) => r.totalHours));
  return (
    <section style={{ ...card, padding: 14 }}>
      <h2 style={{ margin: '0 0 10px', fontSize: 15, color: 'var(--text)' }}>{title}</h2>
      {rows.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>Aucune saisie.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => (
            <div key={r.id ?? '__none'} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 4, fontSize: 13 }}>
              <span style={{ color: r.id ? 'var(--text)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.name}
              </span>
              <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }} title={`Sur la période : ${fmtH(r.periodHours)}`}>
                {fmtH(r.totalHours)}
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · période {fmtH(r.periodHours)}</span>
              </span>
              <div style={{ gridColumn: '1 / -1', height: 6, borderRadius: 999, background: 'var(--glass)' }}>
                <div style={{ width: `${max ? (r.totalHours / max) * 100 : 0}%`, height: '100%', borderRadius: 999, background: 'var(--accent)' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
